import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { getAnalyticsWorkers, getWorkerTimeline } from 'features/operations/opsAnalyticsSlice'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { IOpsWorkerRow } from 'types/opsTimelines.interface'
import { LOSS_BUCKET_TO_UI } from 'pages/Operations/Shared/opsEnumMaps'
import { OpsLossBucket } from 'types/opsAnalytics.interface'
import { BucketLegend, DayStrip } from './DayStrip'
import { StatCell, StatGrid } from './Timelines.styled'

const QUICK_DAYS = ['Today', 'Yesterday']
const PICKER_MIN_DATE = new Date(new Date().getFullYear(), 5, 2)

const apiDateForLabel = (label: string, picked: Date | null): string => {
  if (picked) return picked.toISOString().slice(0, 10)
  const d = new Date()
  if (label === 'Yesterday') d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const workerColumns: IHonestTableColumn<IOpsWorkerRow>[] = [
  {
    id: 'worker',
    header: 'Worker',
    sticky: true,
    cell: (item) => <BodyText weight='600'>{item.workerName}</BodyText>,
  },
  {
    id: 'loads',
    header: 'Loads',
    align: 'right',
    cell: (item) => <BodyText>{item.loads}</BodyText>,
  },
  {
    id: 'grade',
    header: 'Grade',
    align: 'right',
    cell: (item) => <BodyText>{item.grade ?? '—'}</BodyText>,
  },
  {
    id: 'points',
    header: 'Points',
    align: 'right',
    cell: (item) => <BodyText>{item.rewardPoints}</BodyText>,
  },
]

export const WorkerTimelines = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { workers, workerTimeline, loaded, isLoading } = useSelector((s: RootState) => s.opsAnalytics)
  const [selectedDay, setSelectedDay] = useState('Yesterday')
  const [pickedDate, setPickedDate] = useState<Date | null>(null)

  const queryDate = apiDateForLabel(selectedDay, pickedDate)

  useEffect(() => {
    dispatch(getAnalyticsWorkers({ date: queryDate }))
  }, [dispatch, queryDate])

  useEffect(() => {
    if (!id) return
    dispatch(getWorkerTimeline({ id: Number(id), params: { from: queryDate, to: queryDate } }))
  }, [dispatch, id, queryDate])

  const rows: IOpsWorkerRow[] = useMemo(
    () =>
      workers.map((w) => ({
        id: w.workerId,
        workerId: w.workerId,
        workerName: w.name,
        role: '—',
        dateLabel: selectedDay,
        loginAt: '—',
        logoutAt: '—',
        segments: [],
        breaksMin: 0,
        tasksDone: 0,
        loads: w.loads,
        issuesReported: 0,
        grade: w.grade,
        rewardPoints: w.points,
        activeHours: 0,
        unassignedHours: 0,
      })),
    [workers, selectedDay],
  )

  const timelineSegments = useMemo(
    () =>
      (workerTimeline?.segments ?? []).map((seg, index) => ({
        id: index + 1,
        fromMin: 0,
        toMin: 60,
        bucket: (LOSS_BUCKET_TO_UI[seg.bucket] ?? 'NO_TASK') as OpsLossBucket,
        activity: null,
        taskName: null,
        taskId: null,
        location: `${seg.from} – ${seg.to}`,
        note: null,
      })),
    [workerTimeline],
  )

  if (id) {
    const worker = workers.find((w) => w.workerId === Number(id))
    const name = worker?.name ?? `Worker ${id}`
    return (
      <>
        <NavBar />
        <PageWrapper>
          <H1WithBack title={name} arrow onClick={() => navigate('/operations/analytics/workers')} />
          {loaded && !isLoading && !timelineSegments.length ? (
            <NoData text='No timeline recorded for this worker on the selected day.' />
          ) : (
            <Container>
              <ContainerHeader>
                <TitleText>{selectedDay}</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.75rem'>
                {timelineSegments.length > 0 && (
                  <DayStrip
                    segments={timelineSegments}
                    dateLabel={name}
                    variant='worker'
                    startHour={6}
                    windowHours={12}
                  />
                )}
                <BucketLegend />
                {worker && (
                  <StatGrid>
                    <StatCell>
                      <OtherText grey>Loads</OtherText>
                      <BodyText weight='600'>{worker.loads}</BodyText>
                    </StatCell>
                    <StatCell>
                      <OtherText grey>Grade</OtherText>
                      <BodyText weight='600'>{worker.grade}</BodyText>
                    </StatCell>
                    <StatCell>
                      <OtherText grey>Points</OtherText>
                      <BodyText weight='600'>{worker.points}</BodyText>
                    </StatCell>
                  </StatGrid>
                )}
              </ContainerContent>
            </Container>
          )}
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Head1>Worker timelines</Head1>
        <Flex mb='16' center gap='0.75rem' wrap>
          <TabsContainer>
            {QUICK_DAYS.map((label) => (
              <Tabs key={label} active={selectedDay === label} onClick={() => { setSelectedDay(label); setPickedDate(null) }}>
                {label}
              </Tabs>
            ))}
          </TabsContainer>
          <CalendarInput
            name='timelineDay'
            placeholder='Jump to date'
            width='12rem'
            nomarg
            defaultValue={pickedDate}
            minDate={PICKER_MIN_DATE}
            maxDate={new Date()}
            onChange={(date) => {
              setPickedDate(date)
              if (date) setSelectedDay(date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }))
            }}
          />
        </Flex>
        {loaded && !isLoading && !rows.length ? (
          <NoData text='Timelines are computed at shift close once connected.' />
        ) : (
          <HonestTable
            columns={workerColumns}
            data={rows}
            keyField='id'
            emptyMessage='No records for this day.'
            onClick={(item) => navigate(`/operations/analytics/workers/${item.workerId}`)}
          />
        )}
      </PageWrapper>
    </>
  )
}
